import React, { useCallback, useEffect, useRef } from 'react'
import { getJson } from '../../utils/http'
import { MapContainer } from './map/MapContainer'
import { DraggableBottomSheet } from './DraggableBottomSheet'
import { FloatingNurseryItem } from './map/FloatingNurseryItem'
import { JpeSearchBar } from './search-elements/SearchBar'
import { JpeSearchModal } from './search-elements/SearchModal'
import { FilterModal } from './search-elements/FilterModal'
import { useSearchStore } from '../../store/jpeTopPageStore'
import { calcMaxNumberOfChildren } from '../search-bar/utils'
import type { NurseryItemData } from './nureseries/type'
import { addNumericParam, addArrayParam, addParamIfExists } from './utils'

export const JpeTopPage: React.FC = () => {
  const {
    searchData,
    loadFromUrl,
    filterData,
    loadFiltersFromUrl,
    selectedSort,
    loadSortFromUrl,
    loadPageFromUrl,
    setNurseries,
    setLoading,
    setAliases,
    selectedNursery,
    isDesktop,
    setIsDesktop,
    setMapContainerRef,
    clearCheckInMonthsCache,
  } = useSearchStore()

  const mapContainerRef = useRef<HTMLDivElement>(null)

  // Transform API response to NurseryItemData
  const transformNurseryData = useCallback((apiNursery: any): NurseryItemData => {
    const formatChildcareTime = () => {
      const startTime = apiNursery.childcareStartTime
      const endTime = apiNursery.childcareEndTime
      if (!startTime || !endTime) return '-'
      // [issue#2756] 英語画面なので区切りは半角ハイフン（全角「〜」は日本語の表記）
      return `${startTime} - ${endTime}`
    }
    const result = {
      id: apiNursery.id,
      kintoneId: apiNursery.kintoneId,
      lpUrl: apiNursery.lpUrl,
      availabilityUrl: apiNursery.availabilityUrl,
      keyVisualImages: apiNursery.keyVisualImages,
      badge: apiNursery.badge,
      feature: apiNursery.feature || [],
      alias: apiNursery.alias || '',
      name: apiNursery.name || apiNursery.alias || 'Unknown',
      location: `${Number(apiNursery.fromAirport)}h access from ${apiNursery.airportName || ''} (via ${
        apiNursery.airportTransfer || ''
      })`,
      acceptClass: apiNursery.acceptClass,
      programHours: formatChildcareTime(),
      reviewCount: apiNursery.reviewCount || 0,
      highlights: apiNursery.highlights,
      checkInMonths: apiNursery.checkInMonths,
      latitude: Number(apiNursery.latitude) || 0,
      longitude: Number(apiNursery.longitude) || 0,
      prefecture: apiNursery.prefecture || '',
      municipality: apiNursery.municipality || '',
    }
    return result
  }, [])

  // Build filter params helper
  const buildFilterParams = useCallback(
    (params: Record<string, string>) => {
      if (!filterData) return

      addArrayParam(params, 'room_environment', filterData.mobilityConditions)
      addArrayParam(params, 'allergies_en', filterData.mealService)
      addArrayParam(params, 'feature', filterData.nurseryFeatures)
      addArrayParam(params, 'room_type_en', filterData.roomType)
      addArrayParam(params, 'kitchen_en', filterData.kitchen)
      addArrayParam(params, 'bathroom_en', filterData.bathroom)
      addParamIfExists(params, 'text_search', filterData.searchText)
    },
    [filterData],
  )

  // Fetch nurseries from API
  const fetchNurseries = useCallback(async () => {
    try {
      setLoading(true)
      // Clear checkInMonths cache when fetching new nurseries (filter/search change)
      clearCheckInMonthsCache()

      // Build query params from searchData
      const params: Record<string, string> = { jpe: 'true' }
      // Add search params - numeric values
      addNumericParam(params, 'maximum_number_of_adults', searchData.number_of_adults)
      addNumericParam(params, 'number_of_preschoolers_1', searchData.number_of_preschoolers_1)
      addNumericParam(params, 'number_of_students_1', searchData.number_of_students_1)

      // Add search params - strings and arrays
      addParamIfExists(params, 'stay_day', searchData.stay_day)
      addArrayParam(params, 'plan_month', searchData.plan_month)
      addArrayParam(params, 'accept_class', searchData.accept_class, (item) => item.value)
      addParamIfExists(params, 'text_search', searchData.text_search)

      // Complex array transformation for preschooler birthdays
      let numberOfStudyAbroad = 0
      if (searchData.birthdays.length > 0) {
        const validBirthdays = searchData.birthdays.filter((bd) => bd.year && bd.month && bd.day)
        const birthdays = validBirthdays.map((bd) => `${bd.year}-${parseInt(bd.month)}-${parseInt(bd.day)}`)
        addArrayParam(params, 'birthdays', birthdays)
        numberOfStudyAbroad = validBirthdays.length
        if (numberOfStudyAbroad > 0) {
          params.number_of_study_abroad = String(numberOfStudyAbroad)
        }
      }

      const maxChildren = calcMaxNumberOfChildren(
        numberOfStudyAbroad,
        searchData.number_of_preschoolers_1,
        searchData.number_of_students_1,
      )
      addNumericParam(params, 'maximum_number_of_children', maxChildren)

      // Add filter params
      buildFilterParams(params)

      // Add sort param from URL
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search)
        const sortParam = urlParams.get('sort')
        if (sortParam && sortParam !== 'recommended') {
          params.sort = sortParam
        }
      }

      const data = await getJson('/search/nurseries.json', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params,
      })
      const apiNurseries = data.nurseries || []
      const aliases = data.aliases || []
      setAliases(aliases)
      const transformedNurseries = apiNurseries.map((nursery: any) => transformNurseryData(nursery))
      setNurseries(transformedNurseries, data.totalCount || 0, data.resultCount || 0)
    } catch {
      setNurseries([], 0, 0)
    } finally {
      setLoading(false)
    }
  }, [
    searchData,
    filterData,
    transformNurseryData,
    setNurseries,
    setLoading,
    buildFilterParams,
    clearCheckInMonthsCache,
    setAliases,
  ])
  useEffect(() => {
    loadFromUrl()
    loadFiltersFromUrl()
    loadSortFromUrl()
    loadPageFromUrl()
  }, [loadFromUrl, loadFiltersFromUrl, loadSortFromUrl, loadPageFromUrl])

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768)
    }
    setIsDesktop(window.innerWidth >= 768)

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [setIsDesktop])

  useEffect(() => {
    setMapContainerRef(mapContainerRef.current)
  }, [setMapContainerRef])

  useEffect(() => {
    const handlePopState = () => {
      loadFromUrl()
      loadFiltersFromUrl()
      loadSortFromUrl()
      loadPageFromUrl()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [loadFromUrl, loadFiltersFromUrl, loadSortFromUrl, loadPageFromUrl])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchNurseries()
    }, 100)

    return () => clearTimeout(timer)
  }, [searchData, filterData, selectedSort]) // Watch actual data, not the function

  return (
    <div className={`jpe-top-page w-full relative ${isDesktop ? 'pt-5' : ''}`}>
      {isDesktop ? (
        <>
          <div className="flex gap-15px justify-center w-full overflow-hidden">
            <div className="shrink-0" style={{ width: '270px' }}>
              <div
                style={{
                  margin: '0px 15px',
                }}
              >
                <JpeSearchBar
                  useAbsolutePosition={false}
                  showCurrency={false}
                  showMenu={false}
                  customClassName="!mx-2 border w-full border-gray-200 rounded-lg"
                />
              </div>
              <FilterModal mode="inline" />
            </div>

            <div className="shrink-0" style={{ width: '375px' }}>
              <DraggableBottomSheet />
            </div>

            <div ref={mapContainerRef} className="relative" style={{ width: '745px' }}>
              <MapContainer />
              {selectedNursery && <FloatingNurseryItem />}
            </div>
          </div>
          <JpeSearchModal />
          <FilterModal mode="modal" />
        </>
      ) : (
        // Mobile Layout - bottom sheet with map
        <>
          <div className="w-full h-screen relative">
            <MapContainer />
            <JpeSearchBar />
          </div>
          {!selectedNursery && <DraggableBottomSheet />}

          {/* Property Card Popover - shown when marker is clicked */}
          {selectedNursery && <FloatingNurseryItem />}

          <JpeSearchModal />
          <FilterModal />
        </>
      )}
    </div>
  )
}
